import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ExternalLink, MessageSquareText, Phone, Printer, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/orders/StatusBadge';
import { CancelReasonDialog } from '@/components/orders/CancelReasonDialog';
import { formatUZS } from '@/lib/money';
import { formatDateTime, type LocaleKey } from '@/lib/time';
import { groupOrderItems, itemQty } from '@/lib/orderItems';
import { useUIStore } from '@/stores/uiStore';
import { useAcceptOrder, useCompleteOrder } from '@/hooks/useOrderActions';
import { usePrintReceipt } from '@/hooks/usePrintReceipt';
import { usePrinterStore } from '@/stores/printerStore';
import { isPrinterConnected } from '@/lib/btPrinter';
import type { OrderDTO, OrderStatus } from '@/types/api';

interface OrderDetailProps {
  order: OrderDTO;
  readOnly?: boolean;
  onClose?: () => void;
}

const ACTIVE: OrderStatus[] = ['pending', 'paid', 'preparing'];

export function OrderDetail({ order, readOnly = false, onClose }: OrderDetailProps) {
  const { t } = useTranslation();
  const lang = useUIStore((s) => s.language) as LocaleKey;
  const accept = useAcceptOrder();
  const complete = useCompleteOrder();
  const { print: printReceipt, pending: printing } = usePrintReceipt();
  const autoPrint = usePrinterStore((s) => s.autoPrint);
  const [cancelOpen, setCancelOpen] = useState(false);

  const items = order.items ?? [];

  const handleAccept = () => {
    accept.mutate(
      { orderId: order.id },
      {
        onSuccess: () => toast.success(t('orders.toasts.accepted', { id: order.id })),
        onError: (err) => toast.error(t('orders.toasts.actionFailed', { message: err.message })),
      },
    );
  };

  const handleComplete = () => {
    complete.mutate(
      { orderId: order.id },
      {
        onSuccess: () => {
          toast.success(t('orders.toasts.completed', { id: order.id }));
          // Auto-print silently to the Bluetooth printer when both are set.
          // No fallback to window.print() here — that requires its own user gesture.
          if (autoPrint && isPrinterConnected()) {
            void printReceipt(order).catch(() => {});
          }
          onClose?.();
        },
        onError: (err) => toast.error(t('orders.toasts.actionFailed', { message: err.message })),
      },
    );
  };

  const handlePrint = () => {
    void printReceipt(order).catch((err) => {
      toast.error(t('receipt.failed', { message: (err as Error).message }));
    });
  };

  const isActive = ACTIVE.includes(order.status);
  // Accept/Complete stay hidden in read-only (History) views. Cancel is the
  // exception: a still-active order — e.g. a "preparing" order left over from a
  // previous day — surfaces in History and must remain cancellable. Cancel is
  // only ever hidden once the order is terminal (completed/cancelled/error).
  const showActions = !readOnly && isActive;
  const canCancel = isActive;

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="relative px-6 pb-4 pt-6">
        <button
          onClick={onClose}
          aria-label="close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="label-tracked text-[10px]">Order ticket</div>
        <div className="mt-1 flex items-baseline gap-3">
          <span className="font-mono text-sm text-muted-foreground">N°</span>
          <span className="font-mono text-3xl font-semibold leading-none text-foreground">{order.id}</span>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <StatusBadge status={order.status} />
          <span className="text-xs text-muted-foreground">{formatDateTime(order.createdAt, lang)}</span>
        </div>
      </div>

      <div className="dotted-rule mx-6" />

      {order.tableName && (
        <div className="mx-6 mt-4 flex items-center justify-between rounded-md border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="label-tracked">{t('orders.detail.table')}</span>
          </div>
          <span className="font-medium text-foreground">{order.tableName}</span>
        </div>
      )}

      {order.customerPhone && (
        <button
          onClick={() => navigator.clipboard?.writeText(order.customerPhone)}
          className="mx-6 mt-4 flex items-center justify-between rounded-md border border-border bg-card px-4 py-3 text-left transition hover:border-primary/40"
        >
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span className="label-tracked">{t('orders.detail.phone')}</span>
          </div>
          <span className="num-tabular font-medium text-foreground">{order.customerPhone}</span>
        </button>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {order.comment && (
          <div className="mb-5 rounded-md border border-primary/30 bg-primary/5 px-4 py-3">
            <div className="mb-1 flex items-center gap-2 text-primary">
              <MessageSquareText className="h-4 w-4" />
              <span className="label-tracked">{t('orders.detail.comment')}</span>
            </div>
            <p className="whitespace-pre-wrap break-words text-sm text-foreground">{order.comment}</p>
          </div>
        )}

        <h3 className="label-tracked mb-3">{t('orders.detail.items')}</h3>
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            {t('orders.detail.noItems')}
          </div>
        ) : (
          <ul className="space-y-2">
            {groupOrderItems(items).map(({ drink, modifiers }) => {
              const qty = itemQty(drink);
              return (
                <li key={drink.id} className="rounded-md border border-border bg-card px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-medium text-foreground">
                      {drink.name}
                      {qty > 1 && <span className="text-muted-foreground"> ×{qty}</span>}
                    </div>
                    <div className="numerical num-tabular text-base text-foreground">
                      {formatUZS(drink.price * qty, lang)}
                    </div>
                  </div>
                  {modifiers.length > 0 && (
                    <ul className="mt-2 space-y-1 border-t border-dashed border-border pt-2">
                      {modifiers.map((mod) => {
                        const modQty = itemQty(mod);
                        return (
                          <li
                            key={mod.id}
                            className="flex items-start justify-between gap-3 pl-3 text-xs text-muted-foreground"
                          >
                            <span>
                              {mod.name}
                              {modQty > 1 && <span> ×{modQty}</span>}
                            </span>
                            {mod.price > 0 && (
                              <span className="numerical num-tabular">
                                +{formatUZS(mod.price * modQty, lang)}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="dotted-rule my-5" />

        <div className="space-y-2 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="label-tracked text-foreground">{t('orders.detail.total')}</span>
            <span className="numerical num-tabular text-2xl text-primary">
              {formatUZS(order.productPrice, lang)}
            </span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{t('orders.detail.orderedViaHoopla')}</div>
        </div>

        {order.fiscalLink && (
          <a
            href={order.fiscalLink}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-primary hover:border-primary/40"
          >
            <span className="label-tracked text-[10px]">{t('orders.detail.fiscal')}</span>
            <span className="font-medium">{t('orders.detail.fiscalOpen')}</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="border-t border-dashed border-border bg-card/80 px-6 py-4">
        <div className="flex flex-wrap items-center gap-2">
          {canCancel && (
            <Button variant="outline" onClick={() => setCancelOpen(true)} className="text-destructive">
              {t('orders.actions.cancel')}
            </Button>
          )}
          <Button variant="outline" onClick={handlePrint} disabled={printing}>
            <Printer className="mr-2 h-4 w-4" />
            {t('receipt.print')}
          </Button>
          <div className="flex-1" />
          {showActions && (order.status === 'pending' || order.status === 'paid') && (
            <Button size="lg" onClick={handleAccept} disabled={accept.isPending}>
              {t('orders.actions.accept')}
            </Button>
          )}
          {showActions && order.status === 'preparing' && (
            <Button size="lg" variant="success" onClick={handleComplete} disabled={complete.isPending}>
              {t('orders.actions.complete')}
            </Button>
          )}
        </div>
      </div>

      <CancelReasonDialog
        orderId={order.id}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onCancelled={onClose}
      />
    </div>
  );
}
